/**
 * The committed crime snapshot, shaped for the map. Named apart from `@trip/domain/danger` on
 * purpose: that module holds the rules, this one holds what the web page does with them, and two
 * files called `danger.ts` would make an import ambiguous.
 *
 * The level of every sector comes from `adjustedLevel`, which has already applied the hour and the
 * mode. Nothing here bands a score, scales one or compares one against a threshold.
 */
import {
  adjustedLevel,
  CRIME_LABELS,
  FOOT_WEIGHTS,
  VEHICLE_WEIGHTS,
  type Mode,
  type SectorSnapshot,
} from "@trip/domain/danger";
import { useEffect, useMemo, useState } from "react";

/** Level 1 (calmest) to level 6, green through to a dark red. Index by `level - 1`. */
export const LEVEL_COLORS = [
  "#2e9e5b",
  "#8cc152",
  "#f2d544",
  "#f5a02e",
  "#e5533d",
  "#a3182b",
] as const satisfies readonly [string, string, string, string, string, string];

export type Zone = {
  type: "Feature";
  geometry: SectorSnapshot["geometry"];
  properties: { sector: string; level: number; color: string };
};
export type Zones = { type: "FeatureCollection"; features: Zone[] };

/**
 * A snapshot that has been checked: the sectors, the credit the data's licence requires, and the
 * months its counts cover as a reader says them ("Oct 2025 – Sep 2026"), null if it does not say.
 */
export type Danger = { sectors: SectorSnapshot[]; attribution: string; period: string | null };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** A `YYYY-MM` month as "Oct 2025"; null for anything else. */
const monthSaid = (v: unknown): string | null => {
  const m = typeof v === "string" ? /^(\d{4})-(\d{2})$/.exec(v) : null;
  const name = m ? MONTHS[Number(m[2]) - 1] : undefined;
  return m && name ? `${name} ${m[1]}` : null;
};
/** The snapshot's window, first month to last, both included; null unless both are readable. */
export function periodOf(windowStart: unknown, windowEnd: unknown): string | null {
  const from = monthSaid(windowStart);
  const to = monthSaid(windowEnd);
  return from && to ? `${from} – ${to}` : null;
}

const isNum = (v: unknown): v is number => typeof v === "number";
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const isPosition = (p: unknown): boolean =>
  Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]);
/** A closed ring has at least four positions, the last repeating the first. */
const isRing = (r: unknown): boolean => Array.isArray(r) && r.length >= 4 && r.every(isPosition);
const isPolygon = (p: unknown): boolean => Array.isArray(p) && p.length >= 1 && p.every(isRing);

/**
 * Checked down to the last coordinate: a ring that is too short or holds a text number would be
 * handed to MapLibre, which throws on it while the map is working through its queue of layers,
 * and that would take the routes down with it.
 */
function sectorOk(s: unknown): s is SectorSnapshot {
  if (!isObj(s) || typeof s["sector"] !== "string") return false;
  const g = s["geometry"];
  if (!isObj(g) || g["type"] !== "MultiPolygon" || !Array.isArray(g["coordinates"])) return false;
  if (g["coordinates"].length === 0 || !g["coordinates"].every(isPolygon)) return false;
  const foot = s["foot"];
  const vehicle = s["vehicle"];
  return isObj(foot) && isNum(foot["score"]) && isObj(vehicle) && isNum(vehicle["score"]);
}

/**
 * The snapshot if it is whole, otherwise null. Never throws: a bad asset leaves the map as it
 * was. What the page then says about it is `useDangerSectors`' business, and it is not "nothing":
 * see `DangerStatus`. A snapshot with no credit is as bad as a malformed one, since the licence
 * says every screen that shows the data must name its source.
 */
export function parseSnapshot(raw: unknown): Danger | null {
  try {
    if (!isObj(raw) || !Array.isArray(raw["sectors"]) || raw["sectors"].length === 0) return null;
    const attribution = raw["attribution"];
    if (typeof attribution !== "string" || attribution.trim() === "") return null;
    if (!raw["sectors"].every(sectorOk)) return null;
    return {
      sectors: raw["sectors"] as SectorSnapshot[],
      attribution,
      period: periodOf(raw["windowStart"], raw["windowEnd"]),
    };
  } catch {
    return null;
  }
}

/**
 * The snapshot is a separate chunk, fetched only for a trip whose switch is on: 700 KB of polygons
 * is not something every user should carry for a feature that is absent for them. It is still part
 * of the build, so once the chunk has been fetched it works offline like the rest of the app.
 *
 * Null means it could not be had, whether the import was refused (offline on first use, before the
 * chunk has ever been fetched, is the likeliest way to get here) or what arrived was unusable. The
 * caller must not read that as "nothing to show": see `DangerStatus`.
 */
async function loadDanger(): Promise<Danger | null> {
  try {
    const mod = await import("@trip/domain/crime-snapshot.json");
    const danger = parseSnapshot(mod.default);
    if (!danger) console.error("danger map: the crime snapshot is unusable");
    return danger;
  } catch (error) {
    console.error("danger map: the crime snapshot could not be loaded", error);
    return null;
  }
}

/**
 * Every sector as a feature carrying its level and colour at this hour and mode. A place with no
 * sector has no feature: it is not painted, which is what "no data" looks like on the map.
 */
export function zonesFor(sectors: SectorSnapshot[], hour: number, mode: Mode): Zones {
  const key = mode === "FOOT" ? "foot" : "vehicle";
  return {
    type: "FeatureCollection",
    features: sectors.map((s) => {
      const level = adjustedLevel(s[key].score, hour, mode);
      return {
        type: "Feature",
        geometry: s.geometry,
        properties: { sector: s.sector, level, color: LEVEL_COLORS[level - 1]! },
      };
    }),
  };
}

/** The sector of this name, or null when the snapshot has none by it. */
export function sectorNamed(sectors: SectorSnapshot[], name: string): SectorSnapshot | null {
  return sectors.find((s) => s.sector === name) ?? null;
}

/**
 * The snapshot names each alcaldía as the police data does, in capitals with no accents; this is
 * how a reader writes them. One the list does not know is shown as it came.
 */
const ALCALDIAS: Record<string, string> = {
  "ALVARO OBREGON": "Álvaro Obregón",
  AZCAPOTZALCO: "Azcapotzalco",
  "BENITO JUAREZ": "Benito Juárez",
  COYOACAN: "Coyoacán",
  CUAJIMALPA: "Cuajimalpa",
  CUAUHTEMOC: "Cuauhtémoc",
  "GUSTAVO A MADERO": "Gustavo A. Madero",
  IZTACALCO: "Iztacalco",
  IZTAPALAPA: "Iztapalapa",
  "MAGDALENA CONTRERAS": "Magdalena Contreras",
  "MIGUEL HIDALGO": "Miguel Hidalgo",
  "MILPA ALTA": "Milpa Alta",
  TLAHUAC: "Tláhuac",
  TLALPAN: "Tlalpan",
  "VENUSTIANO CARRANZA": "Venustiano Carranza",
  XOCHIMILCO: "Xochimilco",
};
export const alcaldiaName = (raw: string) => ALCALDIAS[raw] ?? raw;

/** One crime's part in a sector's score. */
export type CrimeShare = {
  crime: string;
  label: string;
  /** Reported over the snapshot's window. */
  count: number;
  weight: number;
  /** `weight × count` per thousand residents: what this crime adds to the score. */
  contribution: number;
};

/**
 * What a sector's score for this mode is made of: one row per crime the mode counts and the
 * sector reported, worst first. The score is the same sum, so the contributions add up to it.
 */
export function zoneBreakdown(sector: SectorSnapshot, mode: Mode): CrimeShare[] {
  const weights = mode === "FOOT" ? FOOT_WEIGHTS : VEHICLE_WEIGHTS;
  return Object.entries(weights)
    .map(([crime, weight]) => {
      const count = sector.crimes[crime] ?? 0;
      return {
        crime,
        label: CRIME_LABELS[crime] ?? crime,
        count,
        weight,
        contribution: ((weight * count) / sector.population) * 1000,
      };
    })
    .filter((r) => r.count > 0)
    .sort((a, b) => b.contribution - a.contribution);
}

/**
 * Where the danger layer stands, which is three things and not two.
 *
 *   - `loading`: nothing has been asked for yet, or the snapshot is on its way. The page says
 *     nothing, because there is nothing true to say yet. A trip whose switch is off is here for
 *     good, which is what keeps it completely wordless.
 *   - `ready`: there are zones, and a credit to show with them.
 *   - `unavailable`: it WAS asked for and could not be had. The page must say so. The spec is
 *     explicit that a safety layer going quiet in a dead spot is worse than none, because the quiet
 *     reads as "fine", and an unavailable layer that looks exactly like a switched-off one is that
 *     quiet. (An earlier version of the plan said "the danger layer simply off". The spec is the
 *     authority, and it forbids that.)
 */
export type DangerStatus = "loading" | "ready" | "unavailable";

/**
 * The zones to paint at this hour and mode, the credit to show with them, and the status. Zones
 * and credit are null unless the status is `ready`; nothing is fetched while `enabled` is false.
 * `sectors` is the snapshot itself, for a caller that needs the level under a point (layer 2's
 * scoring) rather than a picture of every sector; null on the same terms as the zones.
 */
export function useDangerSectors(
  hour: number,
  mode: Mode,
  enabled: boolean,
): {
  zones: Zones | null;
  sectors: SectorSnapshot[] | null;
  attribution: string | null;
  /** The months the counts cover, for the zone panel; null on the same terms as the credit. */
  period: string | null;
  status: DangerStatus;
} {
  const [loaded, setLoaded] = useState<Danger | "unavailable" | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void loadDanger().then((d) => live && setLoaded(d ?? "unavailable"));
    return () => {
      live = false;
    };
  }, [enabled]);
  const usable = enabled && loaded !== null && loaded !== "unavailable" ? loaded : null;
  const hourOfDay = Number.isFinite(hour) ? Math.floor(hour) : hour;
  const zones = useMemo(
    () => (usable ? zonesFor(usable.sectors, hourOfDay, mode) : null),
    [usable, hourOfDay, mode],
  );
  const status: DangerStatus =
    !enabled || loaded === null ? "loading" : usable ? "ready" : "unavailable";
  return {
    zones,
    sectors: usable?.sectors ?? null,
    attribution: usable?.attribution ?? null,
    period: usable?.period ?? null,
    status,
  };
}
